import { template as _$template } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<main class=tapes-screen>`);
import { useNavigate, useSearchParams } from "@solidjs/router";
import { createEffect, createMemo, createSignal, onCleanup, onMount, Show } from "solid-js";
import { DEFAULT_AUDIO_SETTINGS } from "../audio/settings.ts";
import { viewTargetLabel } from "../flight/camera-target.ts";
import { DEFAULT_COCKPIT_SETTINGS } from "../flight/cockpit-settings.ts";
import { cameraModeForCommand, createFlightScene, DEFAULT_CAMERA_MODE, IDLE_COCKPIT_VIEW } from "../flight/flight-scene.ts";
import { FlightSurface } from "../flight/FlightSurface.tsx";
import { createBootStatus } from "../flight/boot-status.ts";
import { createKeyboardControls } from "../flight/keyboard.ts";
import { initialHud } from "../flight/sim-runner.ts";
import { createPlaybackRunner } from "../vcr/playback-runner.ts";
import { createTapeShelf } from "../vcr/tape-shelf-actions.ts";
import { TapeShelf } from "../vcr/TapeShelf.tsx";
import { createVcrControls } from "../vcr/vcr-controls.ts";
import { DEFAULT_SURFACE, FLAT_GROUND } from "@red-opus/engine";
import { NO_CONTACT_HUD, tapeCallsigns, tapeFlightOptions, tapeRegion, viewableFrom } from "../vcr/vcr-session.ts";
import { VcrBar } from "../vcr/VcrBar.tsx";
const CAMERA_LABELS = {
  cockpit: "3 · COCKPIT VIEW",
  chase: "1 · CHASE CAMERA",
  flyBy: "2 · FLY-BY CAMERA"
};
/** `/vcr`'s first screen — `SELECT TAPE TO VIEW:` — split out purely to keep `Vcr()` under the project's function-length gate. */
function TapePickerScreen(props) {
  return (() => {
    var _el$ = _tmpl$();
    _$insert(_el$, _$createComponent(TapeShelf, {
      get tapes() {
        return props.shelf.tapes();
      },
      get error() {
        return props.shelf.error();
      },
      get onLoad() {
        return props.onLoad;
      },
      get onDelete() {
        return props.shelf.remove;
      },
      get onExport() {
        return props.shelf.exportTape;
      },
      get onImport() {
        return props.shelf.importFile;
      },
      get onExit() {
        return props.onExit;
      }
    }));
    return _el$;
  })();
}
/** `/vcr`'s second screen — the flight itself plus the Mission Record control panel — split out purely to keep `Vcr()` under the project's function-length gate. */
function VcrFlightScreen(props) {
  return _$createComponent(FlightSurface, {
    get containerRef() {
      return props.containerRef;
    },
    get hud() {
      return props.hud();
    },
    get combat() {
      return props.combat();
    },
    get aircraft() {
      return tapeFlightOptions(props.loaded()).label;
    },
    get aircraftKey() {
      return tapeFlightOptions(props.loaded()).aircraft;
    },
    get chips() {
      return ["MISSION RECORDER", props.loaded().title];
    },
    get messages() {
      return props.messages();
    },
    get cameraLabel() {
      return CAMERA_LABELS[props.cameraMode()];
    },
    get cameraMode() {
      return props.cameraMode();
    },
    get cockpitView() {
      return props.cockpitView();
    },
    get paused() {
      return !props.controls.position().playing;
    },
    get boot() {
      return props.boot;
    },
    audioSettings: DEFAULT_AUDIO_SETTINGS,
    cockpitSettings: DEFAULT_COCKPIT_SETTINGS,
    onChangeAudio: () => {},
    onChangeCockpit: () => {},
    get onReturnToTitle() {
      return props.onExit;
    },
    settingsVisible: false,
    onToggleSettings: () => {},
    onDismissSettings: () => {},
    hintVisible: false,
    onDismissHint: () => {},
    get killCamOpen() {
      return props.killCamOpen();
    },
    get children() {
      return _$createComponent(VcrBar, {
        get tapeLabel() {
          return props.loaded().name;
        },
        get position() {
          return props.controls.position();
        },
        get rate() {
          return props.controls.rate();
        },
        get viewable() {
          return props.viewable();
        },
        get target() {
          return props.controls.target();
        },
        get cameraLabel() {
          return viewTargetLabel(props.controls.target(), id => tapeCallsigns(props.loaded()).get(id));
        },
        get warning() {
          return props.controls.warning();
        },
        get onPlayPause() {
          return props.controls.playPause;
        },
        get onStop() {
          return props.controls.stop;
        },
        onRewind: () => props.controls.seek(0),
        get onSeek() {
          return props.controls.seek;
        },
        get onRate() {
          return props.controls.setRate;
        },
        get onTarget() {
          return props.controls.pickTarget;
        },
        get onSwitchView() {
          return props.controls.switchView;
        },
        get onExit() {
          return props.onExit;
        },
        get onTapes() {
          return props.onTapes;
        }
      });
    }
  });
}

/**
 * `/vcr` — the Mission Recorder.
 *
 * Two screens behind one route, the way the original had them: the tape picker
 * (`SELECT TAPE TO VIEW:`) and, once a tape is loaded, the Mission Record
 * control panel over the flight itself. `/vcr?tape=NAME` opens straight into
 * playback, which is the shape every other route here uses to carry state
 * (`/fly?m=…`, `/duel?room=…`).
 *
 * AD-015's kill camera is deliberately *kept* here (it is only excluded from
 * `/duel`): a tape of a fight already flown is exactly where watching a kill go
 * all the way in is the point, and nothing about a replay is competitive. This
 * surface offers none of the settings toggles, so it runs the feature on
 * `DEFAULT_EFFECTS_SETTINGS` — on — the way it already runs the propeller disc.
 *
 * There is no separate renderer and no recorded video. A tape drives an
 * `EncounterRunner` (`vcr/playback-runner.ts`) which `createFlightScene` mounts
 * exactly as it mounts `/fly`'s and `/duel`'s, so the replay has the real world,
 * cockpit, cameras, tracers and smoke — because it *is* the simulation, run
 * again from the same stick.
 *
 * The decisions live in `vcr/vcr-controls.ts` and `vcr/tape-shelf-actions.ts`;
 * this is the JSX and the wiring.
 */
export function Vcr() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shelf = createTapeShelf();

  /**
   * A memo resolved during *setup*, never a signal set inside `onMount`. The
   * `<Show>` below renders the flight surface, and the surface is what hands
   * back the container the renderer attaches to — so a tape only known inside
   * `onMount` has no container yet and the replay silently never starts
   * (lessons.md, 2026-08-21).
   */
  const tape = createMemo(() => {
    const requested = searchParams.tape;
    return typeof requested === "string" && requested.length > 0 ? shelf.find(requested) : undefined;
  });

  // No WebGPU gate — see `Fly.tsx`'s own note and `three/renderer-mode.ts`.
  const boot = createBootStatus("initializing");
  const [cameraMode, setCameraMode] = createSignal(DEFAULT_CAMERA_MODE);
  const [cockpitView, setCockpitView] = createSignal(IDLE_COCKPIT_VIEW);
  const [hud, setHud] = createSignal(initialHud());
  const [combat, setCombat] = createSignal(NO_CONTACT_HUD);
  const [messages, setMessages] = createSignal([]);
  const [viewable, setViewable] = createSignal([]);
  const [killCamOpen, setKillCamOpen] = createSignal(false);
  let containerRef;
  let scene;
  let runner;
  const controls = createVcrControls({
    runner: () => runner,
    scene: () => scene,
    cameraMode,
    setCameraMode
  });

  /**
   * A navigation rather than a scene swap. The scene owns a GPU device and a
   * loaded region; rebuilding it in place for a second tape is not worth the
   * lifecycle, and the URL is what carries which tape to open.
   */
  const openTape = name => navigate(`/vcr?tape=${encodeURIComponent(name)}`);
  const runCommand = command => {
    const mode = cameraModeForCommand(command, cameraMode());
    if (mode) {
      setCameraMode(mode);
      scene?.setCameraMode(mode);
      return;
    }
    if (command === "pause") controls.playPause();
  };
  let keyboard;
  onMount(() => {
    keyboard = createKeyboardControls(runCommand);
    onCleanup(() => keyboard?.dispose());
  });

  // Runs after the surface above has rendered and handed back its container,
  // which `onMount` cannot promise for content behind a `<Show>`.
  createEffect(() => {
    const loaded = tape();
    if (!loaded || scene || !containerRef) return;

    // The tape is replayed by re-simulating its recorded inputs, so it has to
    // fly over the same ground the scene draws. The scene fills this ref from
    // the region before the first tick; until then it reads flat, which is what
    // a bare-encounter tape with no region gets for ever.
    const groundRef = {
      current: FLAT_GROUND
    };
    const playback = createPlaybackRunner(loaded, {
      groundHeightAt: (x, z) => groundRef.current.groundHeightAt(x, z),
      surfaceAt: (x, z) => groundRef.current.surfaceAt?.(x, z) ?? DEFAULT_SURFACE
    });
    runner = playback;
    setHud(playback.hud());
    setViewable(viewableFrom(playback, loaded));
    scene = createFlightScene(containerRef, {
      onStatus: boot.report,
      onBootProgress: boot.reportProgress,
      onHud: next => {
        setHud(next);
        setViewable(viewableFrom(playback, loaded));
        controls.sync();
      },
      onCombat: setCombat,
      onCockpitView: setCockpitView,
      onKillCam: setKillCamOpen,
      onRegion: () => {},
      onMessages: incoming => setMessages(current => [...current, ...incoming].slice(-3)),
      // A replay ignores the stick (`playback-runner.ts`); the keys that
      // matter here are the camera ones, and they go through `runCommand`.
      pressedKeys: () => keyboard?.pressed() ?? new Set()
    },
    // Muted: a replay of a fight you have already flown does not need the
    // engine roaring at you, and nothing about the audio engine is recorded.
    // Time compression is the *live* feature; playback has its own rate.
    {
      audio: {
        ...DEFAULT_AUDIO_SETTINGS,
        muted: true
      },
      runner: playback,
      timeCompressionAvailable: false,
      // The tape carries its own briefed region (or none, for a bare-encounter
      // tape) — `mission` is never passed here (`playback` is the runner), so
      // without this the scene would fall back to Verdun for every replay.
      region: tapeRegion(loaded),
      groundRef
    });
    // The original's control panel came up with the tape at the start, waiting
    // for PLAY. So does this one.
    playback.setPaused(true);
    controls.sync();
    onCleanup(() => {
      scene?.dispose();
      scene = undefined;
      runner = undefined;
    });
  });
  return _$createComponent(Show, {
    get when() {
      return tape();
    },
    get fallback() {
      return _$createComponent(TapePickerScreen, {
        shelf: shelf,
        onLoad: openTape,
        onExit: () => navigate("/")
      });
    },
    children: loaded => _$createComponent(VcrFlightScreen, {
      loaded: loaded,
      containerRef: element => {
        containerRef = element;
      },
      hud: hud,
      combat: combat,
      messages: messages,
      cameraMode: cameraMode,
      cockpitView: cockpitView,
      boot: boot,
      viewable: viewable,
      controls: controls,
      killCamOpen: killCamOpen,
      onExit: () => navigate("/"),
      onTapes: () => navigate("/vcr")
    })
  });
}